<script setup>
import { reactive, onMounted, onUnmounted, computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useErrorLogger } from '@/composables/useErrorLogger';
import Header from './components/Header.vue';
import { RouterView } from 'vue-router';
import PulseLoader from 'vue-spinner/src/PulseLoader.vue';
import RingBarsSpinner from 'vue-global-loader/RingBarsSpinner.vue';
import { useProductStore } from '@/stores/productsStore';
import { useAuthStore } from '@/stores/authStore';
import { useQzStore } from '@/stores/qzStore';
import { startRealtime, stopRealtime, isRealtimeConnected, onRealtimeMessage } from './services/realtimeService';
import { startHealthCheck, stopHealthCheck, pingNow, onOnline, onOffline, isOnline } from '@/services/healthCheck';
import OfflineIndicator from './components/OfflineIndicator.vue';
import SyncModal from './components/SyncModal/SyncModal.vue';
import { useOfflineQueueStore } from '@/stores/offlineQueueStore';
import { useOrderStore } from '@/stores/OrderStore';
import { useServedFrom } from '@/composables/useServedFrom';
import { useRegisterSW } from 'virtual:pwa-register/vue';

const authStore = useAuthStore();

// OS standalone window title: clean on production, a marker + host on any other
// build so a non-prod deployment is obvious. The title-bar *colour* (theme-color,
// below) stays dedicated to online/offline — this is a separate signal carried by
// the title text only, so the two never collide.
(function applyServedFromTitle() {
  const { isCanonical, displayHost } = useServedFrom();
  document.title = isCanonical
    ? 'Metactical POS'
    : `⚪ Metactical POS — ${displayHost}`;
})();
const offlineQueue = useOfflineQueueStore();
const route = useRoute();
// TEMP: remove after testing
// const { logError } = useErrorLogger();
// async function testLogError() {
//   await logError({ callStack: 'Error: test error\n  at App.vue:10\n  at runtime-core.js:100', errorDetails: { message: 'Something went wrong', code: 500, component: 'TestButton' } });
//   alert('Error logged!');
// }
const store = useProductStore();
const orderStore = useOrderStore();

// PWA update prompt: needRefresh flips true when a new build is installed and waiting.
const { needRefresh } = useRegisterSW();

// Directly tell the waiting worker to take over, then reload — more reliable than
// plugin's updateServiceWorker(), which can no-op during the autoUpdate→prompt transition.
async function applyUpdate() {
  const count = orderStore.state?.orderItems?.length || 0;
  if (count > 0 && !window.confirm(
    `You have ${count} item(s) in the cart. Updating reloads the app and clears them. Update anyway?`
  )) return;

  const reg = await navigator.serviceWorker?.getRegistration();
  if (reg?.waiting) {
    // Reload as soon as the new worker takes control of the page.
    navigator.serviceWorker.addEventListener('controllerchange', () => window.location.reload(), { once: true });
    reg.waiting.postMessage({ type: 'SKIP_WAITING' });
    // Safety net if controllerchange doesn't fire.
    setTimeout(() => window.location.reload(), 2500);
  } else {
    // No waiting worker (already activated / odd state) — just reload to clear the banner.
    window.location.reload();
  }
}
const qzStore = useQzStore();
const password = reactive({ value: '' });

// Drive the installed-PWA title bar colour from connection state: grey (#f4f5f7, app
// background) when online, red (#dc2626, matches the offline pill) when offline.
function applyTitleBarColor() {
  const color = isOnline.value ? '#f4f5f7' : '#dc2626';
  const metas = document.querySelectorAll('meta[name="theme-color"]');
  if (metas.length === 0) {
    const m = document.createElement('meta');
    m.setAttribute('name', 'theme-color');
    m.setAttribute('content', color);
    document.head.appendChild(m);
  } else {
    metas.forEach(m => m.setAttribute('content', color));
  }
}
watch(isOnline, applyTitleBarColor, { immediate: true });
// Reassert on focus / visibility / SW control change — Chrome can revert a standalone
// window's title bar to the manifest theme_color on these events, which otherwise "removes"
// the red while still offline. Re-applying keeps it consistent.
window.addEventListener('focus', applyTitleBarColor);
document.addEventListener('visibilitychange', applyTitleBarColor);
window.addEventListener('pageshow', applyTitleBarColor);
// Safety net: while offline, keep reasserting red so it can't silently revert to the
// manifest grey between events.
setInterval(() => { if (!isOnline.value) applyTitleBarColor(); }, 2000);

// Function to extract query parameters from the URL
const getQueryParams = () => {
    const urlParams = new URLSearchParams(window.location.search);
    console.log('Full URL:', window.location.href);
    console.log('URLP', urlParams);

    // Check if branchID and userId are in the query parameters
    const branchID = urlParams.get('branchID');
    const userId = urlParams.get('userId');

    // If query parameters are missing, check localStorage
    if (!branchID || !userId) {
        const storedBranchId = localStorage.getItem('branchId');
        const storedUserId = localStorage.getItem('userId');

        if (storedBranchId && storedUserId) {
            console.log('Using branchId and userId from localStorage');
            return {
                branchID: storedBranchId,
                userId: storedUserId,
            };
        }
    }

    // Return query parameters if they exist
    return {
        branchID,
        userId,
    };
};

// Define the initial link based on query parameters or localStorage
const { branchID, userId } = getQueryParams();
const initialLink = branchID && userId ? `branchID=${branchID}&userId=${userId}` : null;

console.log('Initial Link', initialLink);
watch(
  () =>
    (authStore.isUserLoggedIn || authStore.isManagerLoggedIn) &&
    !!authStore.branchId, // branch context ready
  (ready) => {
    console.log("SignalR Watcher: ready =", ready);

    if (ready) {
      if (!isRealtimeConnected()) {
        console.log("SignalR Watcher: starting realtime");

        startRealtime(
          () => authStore.getActiveToken(),
          () => ({
            branchId: authStore.branchId,
            lastUpdatedOn: authStore.lastUpdatedOn
          })
        );
      }
    } else {
      console.log("SignalR Watcher: stopping realtime");
      stopRealtime();
    }
  },
  { immediate: true }
);



onMounted(async () => {
    if (initialLink) {
        await store.initialize(initialLink);

        // If pairing is already done, directly fetch products
        if (store.pairingDone) {
            await store.fetchProducts();
        } else {
            // If pairing is not done, show the pairing form
            console.log('Branch not paired. Showing pairing form.');
        }
    }
});

// Function to handle Enter key press
const handleEnterKeyPress = (event) => {
    if (event.key === 'Enter') {
        store.pairBranch(password.value);
    }
};

// Force Logout
let intervalId;

const checkTokenExpiry = async () => {
  // Never force-logout while offline — the cashier can't log back in without the server,
  // and must be able to keep ringing up offline sales.
  if (!isOnline.value) return;

  // When the 24h JWT expires online, silently refresh it via the stored refresh token
  // (FIX-19) instead of logging out. Only force-logout if the refresh token is also
  // gone/expired (7-day life). The 5-min inactivity lock still handles idle security.
  if (authStore.token && authStore.isTokenExpired(authStore.token)) {
    if (!(await authStore.refreshSession('Cashier'))) authStore.logout('Cashier');
  }

  if (authStore.managerToken && authStore.isTokenExpired(authStore.managerToken)) {
    if (!(await authStore.refreshSession('Manager'))) authStore.logout('Manager');
  }
};

// Computed property to check if QZ is disabled by user
const isQzDisabledByUser = computed(() => {
  return !qzStore.isQzConnected && localStorage.getItem('useQzTrayPrint') === 'false';
});

// Scalable Alert Management
const activeAlerts = computed(() => {
  const alerts = [];

  if (qzStore.isConnecting) {
    alerts.push({
      id: 'qz-connecting',
      type: 'warning',
      icon: 'pi-spinner pi-spin',
      message: 'QZ print connecting',
      class: 'bg-yellow-500'
    });
  }

  if ((authStore.isUserLoggedIn || authStore.isManagerLoggedIn) && (isQzDisabledByUser.value && !qzStore.isConnecting)) {
    alerts.push({
      id: 'qz-disconnected',
      type: 'info',
      icon: 'pi-times-circle',
      message: 'QZ tray is not connected',
      class: 'bg-gray-600'
    });
  }

  if ((authStore.isUserLoggedIn || authStore.isManagerLoggedIn) && authStore.openingStatus !== 'open') {
    alerts.push({
      id: 'shift-closed',
      type: 'error',
      icon: 'pi-exclamation-triangle',
      message: `Shift is ${authStore.openingStatus || 'closed'}. Please open a shift to process orders.`,
      class: 'bg-yellow-500'
    });
  }

  return alerts;
});

onMounted(() => {
  checkTokenExpiry();
  
  if (authStore.isUserLoggedIn) {
    authStore.getOpeningEntryStatus();
  }
  
  // Only connect to QZ if user wants QZ printing and not already connecting
  const savedPreference = localStorage.getItem("useQzTrayPrint");
  // Connect only if there is an explicit true in localStorage
  const userWantsQzPrint = savedPreference === "true";
  
  if (userWantsQzPrint && !qzStore.isConnecting) {
    qzStore.connectQZ(true);
  }
  
  qzStore.initQZSecurity();

  intervalId = setInterval(() => {
    checkTokenExpiry();
  }, 60000);

  // Only run health monitoring once a branch is paired — no heartbeat, no pill, no cues before that.
  // pairBranch() reloads the page, so onMounted re-runs with pairingDone=true after pairing.
  if (store.pairingDone) {
    // Load any locally-queued offline requests + offline-window audit log so the pill
    // badge + queue view reflect them.
    offlineQueue.loadQueue();
    offlineQueue.loadWindows();

    // Health heartbeat — drives the connection-status pill (online/offline/maintenance).
    startHealthCheck();

    // SignalR fan-in: branch maintenance re-checks health; offline-sync status events
    // update the local queue as the backend processes each item (FIX-12).
    onRealtimeMessage((msg) => {
      const payload = msg?.payload;
      if (!payload) return;
      if (payload.eventType === 'branch_maintenance') {
        pingNow();
      } else if (payload.eventType === 'offline_sync_status') {
        offlineQueue.applyServerStatus({
          localId: payload.localId,
          status: payload.status,
          serverInvoiceId: payload.serverInvoiceId,
          detail: payload.detail,
        });
      }
    });

    // Going offline: stamp a stable window start, and stop any pending sync countdown.
    onOffline(() => {
      offlineQueue.markOfflineStart();
      offlineQueue.cancelSync();
    });

    onOnline(async () => {
      // Self-heal: provision this device's signing secret if it's missing, so the next
      // offline orders get signed (and aren't rejected on sync).
      authStore.ensureDeviceRegistered();
      // Self-heal: if the catalog never loaded while offline/unhealthy, reload it so
      // scanning works without a manual refresh.
      if (!store.products || store.products.length === 0) {
        console.log('[offline] back online with empty catalog — reloading products');
        store.fetchProducts();
      }
      // Don't sync on the first blip — start the cooldown; it auto-fires when it elapses.
      offlineQueue.scheduleSyncIfPending();
    });

    // Startup reconciliation: if the app boots already-online with items left pending
    // from a previous session, no offline→online transition will fire — so start the
    // cooldown now once the first heartbeat confirms we're actually online. Sync is
    // authorized per-item by the maker's token, so no one needs to be logged in.
    (async () => {
      await offlineQueue.loadQueue();
      await pingNow();
      if (isOnline.value) {
        // Make sure this device is provisioned with a signing secret before it goes offline.
        authStore.ensureDeviceRegistered();
        offlineQueue.scheduleSyncIfPending();
        // Reconcile any items left "processing" from a previous session whose SignalR
        // status event we may have missed (the order may already be synced server-side).
        offlineQueue.startReconcileLoop();
      }
    })();
  }
});

onUnmounted(() => {
  clearInterval(intervalId);
  stopHealthCheck();
});

</script>

<template>
    <section class="h-screen px-4 relative">
        <!-- Connection status pill + offline queue view — shown only once a branch is paired -->
        <OfflineIndicator v-if="store.pairingDone" />
        <SyncModal v-if="store.pairingDone" />
        <div class="container-xl lg:container m-auto">
            <!-- Standalone routes bypass POS initialization -->
            <template v-if="route.path === '/checkinventory'">
                <RouterView />
            </template>

            <template v-else>

            <!-- Show "You need to pair a branch" if no query params and no localStorage data -->
            <div v-if="!initialLink" class="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex flex-col items-center text-gray-500 py-6">
                <p class="text-lg">You need to pair a branch!</p>
            </div>

            <!-- Show Branch Pairing Form if not paired and query parameters exist -->
            <div v-else-if="!store.pairingDone && initialLink" class="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex flex-col items-center text-gray-500 py-6">
                <!-- Password input -->
                <input
                    v-model="password.value"
                    type="password"
                    placeholder="Enter password"
                    class="form-input"
                    @keydown="handleEnterKeyPress"
                />
                <button @click="store.pairBranch(password.value)" class="p-2 bg-purple-700 text-white rounded mt-4">Pair Branch</button>
            </div>

            <!-- Show Loading Spinner while loading is true -->
            <div v-else-if="store.isLoading" class="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex flex-col items-center text-gray-500 py-6">
                <RingBarsSpinner style="width: 60px;" />
                <p class="mt-4">Loading... {{ store.loadingPercentage }}%</p>
            </div>

            <!-- Product listing when done loading -->
            <div v-else class="">
                <div class="h-screen flex flex-col">
                    <Header />
                    <RouterView />
                </div>
            </div>

            <!-- TEMP: remove after testing -->
            <!-- <button @click="testLogError" class="fixed bottom-20 right-4 z-[200] bg-red-500 text-white text-xs px-3 py-1.5 rounded shadow">
              Test Log Error
            </button> -->

            <!-- PWA update available -->
            <div
                v-if="needRefresh"
                class="fixed top-4 left-1/2 -translate-x-1/2 z-[60] bg-purple-600 text-white px-4 py-2 rounded-xl shadow-lg flex items-center gap-3"
            >
                <i class="pi pi-arrow-circle-up"></i>
                <span class="text-sm font-medium">A new version is available.</span>
                <button
                    @click="applyUpdate"
                    class="bg-white text-purple-700 text-sm font-semibold px-3 py-1 rounded-lg hover:bg-purple-50"
                >
                    Update
                </button>
            </div>

            <div class="fixed bottom-4 left-4 z-50 flex items-start gap-2">
                <div
                    v-for="alert in activeAlerts"
                    :key="alert.id"
                    :class="[alert.class, 'text-white px-3 py-2 rounded-lg shadow-lg flex items-center gap-2 transition-all duration-300']"
                >
                    <i :class="['pi', alert.icon]"></i>
                    <span class="text-sm font-medium">{{ alert.message }}</span>
                </div>
            </div>

            </template>
        </div>
    </section>
</template>

<style scoped>
.form-input {
    width: 300px;
    padding: 0.5rem;
    border: 1px solid #d1d5db;
    border-radius: 0.375rem;
    outline: none;
}

.form-input:focus {
    border-color: #7c3aed; 
    box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.1);
}

.iti {
  --iti-path-flags-1x: url('path/to/flags.webp');
  --iti-path-flags-2x: url('path/to/flags@2x.webp');
  --iti-path-globe-1x: url('path/to/globe.webp');
  --iti-path-globe-2x: url('path/to/globe@2x.webp');
}
</style>